import { useCallback, useState } from 'react'
import { AuthClient } from '@guestgoodie/auth-core'
import { useLingui } from '@lingui/react/macro'

export function useErrorToText() {
  const { t } = useLingui()

  return useCallback(
    (code: string) => {
      switch (code) {
        case 'not_found':
          return t({
            comment:
              "Error message when email and password isn't matching when authenticating/logging in",
            message: 'Email and/or Password doesn`t match'
          })
        case 'invalid_code':
          return t({
            comment:
              'Error message when the email verification code for the sign up is wrong',
            message: 'The verification code isn`t valid, please try again'
          })
        case 'not_a_google_user':
          return t({
            comment:
              "Error message when the user tries to login with a google account, where the email is an existing user, but the user wasn't logged in with Google so far.",
            message:
              'The given user is not a google user, please try to sign-in with email + password.'
          })
        case 'email_already_taken':
          return t({
            comment:
              'Error message on the SignUp when the given email already exists.',
            message: 'The E-Mail is already a user'
          })
        case 'join_when_signedin':
          return t({
            comment:
              "Information message when the user tries to join an account but isn't yet signed in.",
            message: 'Sign-in or create an account to join an organization'
          })
        case 'unknown':
        default:
          return t({
            comment:
              "Error message when an error happens that we didn't expect",
            message:
              'An unknown error happened, please try it again or if it keeps happening write our support team: help@guestgoodie.com.'
          })
      }
    },
    [t]
  )
}

function useErrorCodeToText(error?: string | null) {
  const { t } = useLingui()

  if (!error) return null

  switch (error) {
    case 'not_found':
      return t({
        comment:
          "Error message when email and password isn't matching when authenticating/logging in",
        message: 'Email and/or Password doesn`t match'
      })
    case 'invalid_code':
      return t({
        comment:
          'Error message when the email verification code for the sign up is wrong',
        message: 'The verification code isn`t valid, please try again'
      })
    case 'not_a_google_user':
      return t({
        comment:
          "Error message when the user tries to login with a google account, where the email is an existing user, but the user wasn't logged in with Google so far.",
        message:
          'The given user is not a google user, please try to sign-in with email + password.'
      })
    case 'email_already_taken':
      return t({
        comment:
          'Error message on the SignUp when the given email already exists.',
        message: 'The E-Mail is already a user'
      })
    case 'join_when_signedin':
      return t({
        comment:
          "Information message when the user tries to join an account but isn't yet signed in.",
        message: 'Sign-in or create an account to join an organization'
      })
    case 'unknown':
    default:
      return t({
        comment: "Error message when an error happens that we didn't expect",
        message:
          'An unknown error happened, please try it again or if it keeps happening write our support team: help@guestgoodie.com.'
      })
  }
}

export function useErrorCode(client: AuthClient) {
  const [error, setError] = useState<string | null>(
    client.getError()?.errorCode ?? null
  )

  const text = useErrorCodeToText(error)

  // We expect needsVerification, therefore we don't show it as error
  const needsVerification = error === 'email_verification_needed'
  // Having an invalid_session on the signIn/signUp page is okay so we don't need to show it
  const isInvalidSession = error === 'invalid_session'

  const showError = error && !isInvalidSession && !needsVerification

  const refresh = useCallback(() => {
    setError(client.getError()?.errorCode ?? null)
  }, [client])

  return {
    refresh,
    error: showError ? error : null,
    text: showError ? text : null,
    showVerificationCode: needsVerification
  }
}
